// Packages
import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";

// Components
import { Box, Button, Typography } from "@mui/material";
import ModalContainer from "components/ModalContainer";
import { createColumnHelper } from "@tanstack/react-table";
import TabsField from "components/Tab";
import TableField from "components/Table";
import PendingChildren from '../PendingChildren';

// Img
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Helper
import { getSupportListSendRequestParams, initSupportListTableData } from "../../helper";

// Const
import { defaultTabs, defaultSupportTableData, getSupportListModalColumn } from "../../const";

const SupportListModal = ({
  isError,
  setIsError,
  messages,
  setMessages,
  selectedFW,
  setSelectedFW,
  isOpenSupportListModal,
  setIsOpenSupportListModal,
  setIsOpenReleaseModal
}) => {
  const [uuids] = useState(() => ({
    supportList: uuidv4()
  }));
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const [supportTableData, setSupportTableData] = useState(cloneDeep(defaultSupportTableData));
  const [tabs, setTabs] = useState(cloneDeep(defaultTabs));
  const activeTab = useMemo(() => tabs.find(tabItem => tabItem.isActive), [tabs]);
  const activeSupportTable = supportTableData[activeTab.value];

  const [isLoading, setIsLoading] = useState(false);

  const handelSelectedTargetFwVer = fw => {
    setIsOpenReleaseModal(true);
    setSelectedFW(cloneDeep(fw));
  }

  const supportListTableInstance = useTableInstance({
    tableName: "firmwareSupportListModalTable",
    data: activeSupportTable,
    enableSorting: true,
    enablePagination: true,
    columns: getSupportListModalColumn({
      columnHelper,
      callbacks: {
        handelSelectedTargetFwVer
      }
    }),
  });

  const fetchTableData = () => {
    if (isAuthReady) {
      const { apiKey, restfulParams } = getSupportListSendRequestParams({ auth });

      setIsLoading(true);

      sendRequest(
        apiKey,
        uuids.supportList,
        restfulParams
      );

      setIsLoading(false);
    }
  }

  const handleResetModal = () => {
    setIsLoading(false);
  }

  useEffect(() => {
    fetchTableData();
  }, [isAuthReady]);

  useEffect(() => {
    const response = getData(uuids.supportList);
    const isRespError = !!response?.error || response?.code >= 400;
    const isRespSuccess = !!response?.data || !!response?.result;

    if (isRespError) {
      setIsLoading(false);
      setIsError(true);
      setMessages({
        ...messages,
        error: response?.message || ""
      });

      return;
    }

    if (isRespSuccess) {
      const responseData = response.data || response.result;
      const { accessPoints, switches, routers } = initSupportListTableData({
        responseData
      });

      setSupportTableData({
        accessPoints,
        switches,
        routers
      });

      setIsLoading(false);
    }

  }, [dataMap])

  useEffect(() => {
    return () => {
      handleResetModal();
    }
  }, [])

  return (
    <ModalContainer display={isOpenSupportListModal} modalWidth={"1250px"}>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>

        <Box>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}>
            Support List
          </Typography>
        </Box>

        <Button
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
          onClick={() => setIsOpenSupportListModal(false)}
        >
          <CloseIcon />
        </Button>

      </Box>

      <Box
        sx={{
          width: "100%",
          margin: "20px",
        }}
      >
        <TabsField
          tabVal={activeTab?.value || null}
          handleTabsChange={(e, newValue) => {

            setTabs(prevTabs => {
              return prevTabs.map(tabItem => {
                return {
                  ...tabItem,
                  isActive: tabItem.value === newValue
                }
              })
            })

          }}
          tabData={tabs}
        />
      </Box>

      <Box sx={{ marginBottom: "20px" }}>
        <TableField
          {...supportListTableInstance}
          pendingChildren={
            <PendingChildren
              {...{
                isLoading,
                list: activeSupportTable,
                description: "No data"
              }}
            />
          }
        />
      </Box>

    </ModalContainer>
  );
};

export default SupportListModal;